import { useEffect, useMemo } from 'react'
import { useDispatch, useStore } from 'react-redux'
import { sessionApi } from '@/entities/session'
import { baseApi, endSession } from '@/shared/api'
import { WS_CLOSE, wsClient, type WsClient } from '@/shared/ws'
import { useAppSelector } from '../store'
import {
  registerWsSubscriptions,
  type WsApiState,
  type WsDispatch,
} from './wsSubscriptions'

/**
 * Единственный сокет приложения. Поднимается при живом сеансе, закрывается при выходе,
 * переподписывается при смене юрлица. Второго сокета в приложении нет — фичи только
 * подписываются на топики через реестр.
 *
 * Сеанс читается из кеша селектором (`getSession.select()`), запрос не инициируется: на входе
 * и стенде кеш пуст — сокета нет; как только ворота сеанса загрузят `/auth/me`, сокет встаёт.
 *
 * Живёт в слое app: он собирает `shared/ws` с entities (их api правит реестр) и со стором —
 * слайсы одного слоя друг друга не трогают. Своего UI нет: индикатор связи — в шапке.
 */
export function WsProvider({ client = wsClient }: { client?: WsClient }) {
  const dispatch = useDispatch<WsDispatch>()
  const store = useStore<WsApiState>()

  const selectSession = useMemo(() => sessionApi.endpoints.getSession.select(), [])
  const session = useAppSelector(selectSession).data

  const userId = session?.user.id ?? null
  const unp = session?.user.legalEntity?.unp ?? null

  // Сокет живёт, пока есть сеанс: встаёт при входе, закрывается при выходе.
  useEffect(() => {
    if (userId === null) return
    client.connect()
    return () => client.disconnect()
  }, [client, userId])

  // Подписки контекста: смена юрлица пересобирает их (старые топики снимаются, новые встают).
  useEffect(() => {
    if (userId === null) return
    return registerWsSubscriptions(client, dispatch, store.getState, { unp, userId })
  }, [client, dispatch, store, userId, unp])

  // Отзыв сеанса (код 4401 или событие session.revoked) — на вход, как baseQuery при 401.
  useEffect(() => {
    const toLogin = () => {
      endSession()
      dispatch(baseApi.util.resetApiState())
    }
    const offClose = client.onClose((code) => {
      if (code === WS_CLOSE.unauthorized) toLogin()
    })
    const offEvent = client.onEvent((envelope) => {
      if (envelope.type === 'session.revoked') toLogin()
    })
    return () => {
      offClose()
      offEvent()
    }
  }, [client, dispatch])

  return null
}
